// Feb 2026 · Quick-preview dialog for a meeting row.
//
// Why not just navigate to the detail page?
// On the Meetings list a PM frequently wants to scan "what was this
// one about, who was there, any action items" without losing their
// current filter + scroll position.  This dialog shows everything
// the detail page shows at a glance, with an "Open full page" CTA
// if they do want to drill in.
//
// Data strategy:
//   • seed with the row object we already have (title, when, host,
//     client/project, action-item counts)
//   • lazy-fetch the full meeting + action items when opened so the
//     notes / mom / attendees surface too (no cost until opened)
//
import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Calendar, Clock, MapPin, Users, FileText, CheckSquare, ArrowUpRight, Star, Repeat, EyeOff, Video, Phone, MessageCircle, Mail, MessageSquare, Building, AlertTriangle } from "lucide-react";
import api from "@/lib/api";
import { fmtRelative } from "@/lib/format";

const MODE_META = {
  video_call:   { label: "Video call",   Icon: Video },
  audio_call:   { label: "Audio call",   Icon: Phone },
  in_person:    { label: "In-person",    Icon: MapPin },
  chat_dm:      { label: "Chat / DM",    Icon: MessageCircle },
  email_thread: { label: "Email thread", Icon: Mail },
  whatsapp:     { label: "WhatsApp",     Icon: MessageSquare },
  site_visit:   { label: "Site visit",   Icon: Building },
  escalation:   { label: "Escalation",   Icon: AlertTriangle },
};

export default function MeetingPreviewDialog({ meeting, onClose, onOpenFull }) {
  const [full, setFull] = useState(null);
  const [actions, setActions] = useState([]);
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    let alive = true;
    if (!meeting?.id) return;
    setBusy(true);
    (async () => {
      try {
        const [a, b] = await Promise.all([
          api.get(`/meetings/${meeting.id}`).catch(() => ({ data: null })),
          api.get(`/meetings/${meeting.id}/action-items`).catch(() => ({ data: [] })),
        ]);
        if (!alive) return;
        setFull(a.data || null);
        setActions(b.data || []);
      } finally {
        if (alive) setBusy(false);
      }
    })();
    return () => { alive = false; };
  }, [meeting?.id]);

  if (!meeting) return null;
  // Defensive: `/meetings/{id}` can occasionally return nested objects
  // on fields the list endpoint returns as scalars (e.g. series info).
  // Coerce the fields we render as strings so a surprise object never
  // crashes the dialog's render tree (React 18 throws on object child).
  const _str = (v) => {
    if (v == null) return "";
    if (typeof v === "string" || typeof v === "number") return String(v);
    if (typeof v === "object") return v.name || v.title || v.label || v.email || "";
    return String(v);
  };
  const m = { ...meeting, ...(full || {}) };
  const title       = _str(m.title) || "Untitled meeting";
  const hostName    = _str(m.host_name);
  const clientName  = _str(m.client_name);
  const brandName   = _str(m.brand_name);
  const projectName = _str(m.project_name);
  const description = _str(m.description);
  const agenda      = _str(m.agenda);
  const mom         = _str(m.mom);
  const location    = _str(m.location);
  const meetingLink = _str(m.meeting_link);
  const mode = MODE_META[m.mode] || null;
  const ModeIcon = mode?.Icon;
  const when = m.scheduled_at ? new Date(m.scheduled_at) : null;

  return (
    <Dialog open={!!meeting} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-3xl rounded-none p-0 overflow-hidden" data-testid="meeting-preview-dialog">
        <DialogHeader className="px-4 pt-4 pb-2 border-b border-border bg-muted/30">
          <DialogTitle className="text-base pr-8">
            <span className="inline-flex items-center gap-1.5 flex-wrap">
              {m.series_id && <Repeat className="w-3.5 h-3.5 text-muted-foreground" title="Recurring series" />}
              {m.is_private && <EyeOff className="w-3.5 h-3.5 text-purple-600" title="Private meeting" />}
              <span>{title}</span>
              {typeof m.rating === "number" && m.rating > 0 && (
                <span className="inline-flex items-center gap-0.5 text-xs text-amber-600 border border-amber-300 px-1.5 py-0.5">
                  <Star className="w-3 h-3 fill-current" /> {m.rating}/5
                </span>
              )}
              {m.is_external
                ? <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 border border-sky-300 text-sky-700 bg-sky-50">External</span>
                : <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 border border-border text-muted-foreground">Internal</span>}
              {m.is_logged
                ? <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 border border-emerald-300 text-emerald-700 bg-emerald-50">Logged</span>
                : <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 border border-amber-300 text-amber-700 bg-amber-50">Scheduled</span>}
            </span>
          </DialogTitle>
        </DialogHeader>

        <div className="px-4 py-3 space-y-3 max-h-[70vh] overflow-y-auto" data-testid="meeting-preview-body">
          {/* Top strip — when / mode / host / duration */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
            <PreviewStat icon={<Calendar className="w-3.5 h-3.5" />} label="When">
              {when
                ? <span>{when.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}<br/><span className="text-muted-foreground">{fmtRelative(m.scheduled_at)}</span></span>
                : "—"}
            </PreviewStat>
            <PreviewStat icon={<Clock className="w-3.5 h-3.5" />} label="Duration">
              {(m.duration_minutes || 30) + "m"}
            </PreviewStat>
            <PreviewStat icon={ModeIcon ? <ModeIcon className="w-3.5 h-3.5" /> : null} label="Mode">
              {mode?.label || m.mode || "—"}
            </PreviewStat>
            <PreviewStat icon={<Users className="w-3.5 h-3.5" />} label="Host">
              {hostName || "—"}
            </PreviewStat>
          </div>

          {/* Client / Project / Brand */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs border-t pt-3">
            <PreviewStat label="Client">{clientName || "—"}</PreviewStat>
            <PreviewStat label="Brand">{brandName || "—"}</PreviewStat>
            <PreviewStat label="Project">{projectName || "—"}</PreviewStat>
          </div>

          {/* Location / Link */}
          {(location || meetingLink) && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs border-t pt-3">
              {location && <PreviewStat icon={<MapPin className="w-3.5 h-3.5" />} label="Location">{location}</PreviewStat>}
              {meetingLink && (
                <PreviewStat icon={<Video className="w-3.5 h-3.5" />} label="Link">
                  <a href={meetingLink} target="_blank" rel="noreferrer" className="text-primary hover:underline break-all">{meetingLink}</a>
                </PreviewStat>
              )}
            </div>
          )}

          {/* Attendees */}
          <div className="border-t pt-3">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1 flex items-center gap-1">
              <Users className="w-3 h-3" /> Attendees ·
              <span className="font-mono">{(m.attendee_user_ids || []).length}</span> internal
              {" "}·{" "}
              <span className="font-mono">{(m.external_attendees || []).length}</span> external
            </div>
            {Array.isArray(m.attendee_names) && m.attendee_names.length > 0 && (
              <div className="flex flex-wrap gap-1 text-[11px]">
                {m.attendee_names.map((n, i) => (
                  <span key={i} className="px-1.5 py-0.5 border border-border bg-background">{String(typeof n === "string" ? n : (n?.name || n?.email || "attendee"))}</span>
                ))}
              </div>
            )}
            {Array.isArray(m.external_attendees) && m.external_attendees.length > 0 && (
              <div className="flex flex-wrap gap-1 text-[11px] mt-1">
                {m.external_attendees.map((n, i) => (
                  <span key={i} className="px-1.5 py-0.5 border border-sky-300 bg-sky-50 text-sky-800">{String(typeof n === "string" ? n : (n?.name || n?.email || "external"))}</span>
                ))}
              </div>
            )}
          </div>

          {/* Description / Agenda */}
          {(description || agenda) && (
            <div className="border-t pt-3">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1 flex items-center gap-1">
                <FileText className="w-3 h-3" /> {agenda ? "Agenda" : "Description"}
              </div>
              <div className="text-xs whitespace-pre-wrap leading-relaxed">{description || agenda}</div>
            </div>
          )}

          {/* MoM — minutes of meeting notes */}
          {mom && (
            <div className="border-t pt-3">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Minutes of meeting</div>
              <div className="text-xs whitespace-pre-wrap leading-relaxed bg-muted/40 p-2 border border-border">{mom}</div>
            </div>
          )}

          {/* Action items */}
          <div className="border-t pt-3">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1 flex items-center gap-1">
              <CheckSquare className="w-3 h-3" /> Action items ·
              <span className="font-mono">{(m.action_items_open ?? actions.filter((a) => a.status !== "done").length) || 0}</span> open
              {" / "}
              <span className="font-mono">{(m.action_items_total ?? actions.length) || 0}</span> total
            </div>
            {busy && <div className="text-[11px] text-muted-foreground italic">Loading…</div>}
            {!busy && actions.length === 0 && <div className="text-[11px] text-muted-foreground italic">No action items.</div>}
            {!busy && actions.length > 0 && (
              <ul className="space-y-1">
                {actions.slice(0, 20).map((a) => (
                  <li key={a.id} className="flex items-start gap-2 text-xs border border-border px-2 py-1 bg-background">
                    <span className={`shrink-0 w-3.5 h-3.5 mt-0.5 border ${a.status === "done" ? "bg-emerald-500 border-emerald-500 text-white" : "border-border"} inline-flex items-center justify-center`}>
                      {a.status === "done" && <CheckSquare className="w-2.5 h-2.5" />}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className={`truncate ${a.status === "done" ? "line-through text-muted-foreground" : ""}`}>{_str(a.title) || _str(a.description) || "(no title)"}</div>
                      <div className="text-[10px] text-muted-foreground">
                        {_str(a.assignee_name) || "unassigned"}
                        {a.due_date && ` · due ${String(a.due_date).slice(0, 10)}`}
                        {a.status && ` · ${_str(a.status)}`}
                      </div>
                    </div>
                  </li>
                ))}
                {actions.length > 20 && (
                  <li className="text-[10px] text-muted-foreground italic text-center py-1">
                    + {actions.length - 20} more action item{actions.length - 20 === 1 ? "" : "s"} — open the full page to see all
                  </li>
                )}
              </ul>
            )}
          </div>

          {(m.missing_details || []).length > 0 && (
            <div className="border-t pt-3">
              <div className="text-xs text-rose-700">
                <strong>Missing:</strong> {(m.missing_details || []).join(", ")}
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between gap-2 px-4 py-2.5 border-t border-border bg-muted/30">
          <span className="text-[10px] text-muted-foreground">
            {busy ? "Loading full meeting…" : "Everything you need at a glance."}
          </span>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={onClose} className="rounded-none h-8 text-xs" data-testid="meeting-preview-close">
              Close
            </Button>
            <Button size="sm" onClick={() => { onOpenFull?.(m.id); }} className="rounded-none h-8 text-xs" data-testid="meeting-preview-open-full">
              <ArrowUpRight className="w-3.5 h-3.5 mr-1" /> Open full page
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function PreviewStat({ icon, label, children }) {
  return (
    <div className="min-w-0">
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1">
        {icon}<span>{label}</span>
      </div>
      <div className="text-xs truncate">{children}</div>
    </div>
  );
}
